import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, Space, Spin, Switch, Tag, message } from 'antd';
import { GoogleOutlined, SaveOutlined } from '@ant-design/icons';
import { get, put } from '../../api/request';
import { API_ENDPOINTS } from '../../api/config';
import './LoginConfigPage.css';

interface LoginConfig {
  id: number;
  config_key: string;
  google_enabled: boolean;
  google_web_client_id: string;
  google_android_client_id: string;
  google_ios_client_id: string;
  google_ios_url_scheme: string;
  google_authorized_domain: string;
  google_callback_origin: string;
}

const LoginConfigPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<LoginConfig | null>(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await get<LoginConfig>(API_ENDPOINTS.login_config.config);
      setConfig(data);
      form.setFieldsValue(data);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载登录配置失败');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await put(API_ENDPOINTS.login_config.config, {
        google_enabled: values.google_enabled,
        google_web_client_id: values.google_web_client_id,
        google_android_client_id: values.google_android_client_id,
        google_ios_client_id: values.google_ios_client_id,
        google_ios_url_scheme: values.google_ios_url_scheme,
        google_authorized_domain: values.google_authorized_domain,
        google_callback_origin: values.google_callback_origin,
      });
      message.success('登录配置已保存');
      await loadConfig();
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="login-config-page">
      <h1 className="page-title">登录配置</h1>

      <Card bordered={false} className="login-config-card">
        <Spin spinning={loading}>
          {config && (
            <Space wrap className="login-config-summary">
              <Tag color={config.google_enabled ? 'green' : 'red'}>
                Google: {config.google_enabled ? '已启用' : '已关闭'}
              </Tag>
              <Tag color={config.google_web_client_id ? 'blue' : 'orange'}>Web Client ID</Tag>
              <Tag color={config.google_android_client_id ? 'geekblue' : 'orange'}>Android Client ID</Tag>
              <Tag color={config.google_ios_client_id ? 'purple' : 'orange'}>iOS Client ID</Tag>
            </Space>
          )}

          <Alert
            type="info"
            showIcon
            message="这里统一维护 Google 登录配置。前台 Web、iOS、Android 都从同一份配置读取 Client ID；正式域名接入后，只需要在 Google Cloud 放行域名并在这里保存新配置。"
            description="Client ID 会参与后端 token 校验，配置错误时登录会被拒绝。iOS URL Scheme 用于原生端回跳。"
            style={{ marginBottom: 16 }}
          />

          <Form form={form} layout="vertical" initialValues={{ google_enabled: true }}>
            <Form.Item label="启用 Google 登录" name="google_enabled" valuePropName="checked">
              <Switch checkedChildren="启用" unCheckedChildren="关闭" />
            </Form.Item>

            <div className="login-config-grid">
              <Form.Item
                label="Web Client ID"
                name="google_web_client_id"
                rules={[{ required: true, message: '请输入 Web Client ID' }]}
              >
                <Input prefix={<GoogleOutlined />} placeholder="xxxx.apps.googleusercontent.com" />
              </Form.Item>

              <Form.Item
                label="Android Client ID"
                name="google_android_client_id"
                rules={[{ required: true, message: '请输入 Android Client ID' }]}
              >
                <Input prefix={<GoogleOutlined />} placeholder="xxxx.apps.googleusercontent.com" />
              </Form.Item>

              <Form.Item
                label="iOS Client ID"
                name="google_ios_client_id"
                rules={[{ required: true, message: '请输入 iOS Client ID' }]}
              >
                <Input prefix={<GoogleOutlined />} placeholder="xxxx.apps.googleusercontent.com" />
              </Form.Item>

              <Form.Item
                label="iOS URL Scheme"
                name="google_ios_url_scheme"
                rules={[{ required: true, message: '请输入 iOS URL Scheme' }]}
              >
                <Input placeholder="com.googleusercontent.apps.xxxx" />
              </Form.Item>
            </div>

            <div className="login-config-grid">
              <Form.Item
                label="授权域名"
                name="google_authorized_domain"
                extra="默认与 Google Cloud 的 redirect_uris、javascript_origins 保持一致。"
              >
                <Input placeholder="shengyu.example.com" />
              </Form.Item>

              <Form.Item
                label="回调来源 Origin"
                name="google_callback_origin"
                extra="默认与 Google Cloud 的 javascript_origins 保持一致；Web 端使用 Google Identity Services，不需要额外回调接口。"
              >
                <Input placeholder="https://shengyu.example.com" />
              </Form.Item>
            </div>
          </Form>
        </Spin>
      </Card>

      <div className="login-config-actions">
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
          保存配置
        </Button>
      </div>
    </div>
  );
};

export default LoginConfigPage;
